import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { renderLinkedText } from "@/lib/content";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { PageSeo } from "@/components/PageSeo";

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export default function AboutPage() {
  const { content } = useContent();
  const a = content.about_page;

  return (
    <div className="grain bg-[#FAF8F5]" data-testid="about-page">
      <PageSeo path="/about" />
      <Navbar />
      <main>
        <section className="bg-[#1C1917] pb-20 pt-40 lg:pt-48">
          <div className="mx-auto max-w-5xl px-6 lg:px-10">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-8 inline-flex items-center gap-3 rounded-full border border-[#FAF8F5]/25 bg-black/20 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#FAF8F5]/90 backdrop-blur-md"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#C5A059]" />
              {a.kicker}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-4xl font-medium leading-[1.1] tracking-tight text-[#FAF8F5] sm:text-5xl"
              data-testid="about-page-h1"
            >
              {renderLinkedText(a.h1)}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.25 }}
              className="mt-6 font-display text-xl italic text-[#C5A059] md:text-2xl"
            >
              {a.intro}
            </motion.p>
          </div>
        </section>

        <section className="mx-auto max-w-4xl px-6 py-24 lg:px-10">
          <div className="space-y-14">
            {a.blocks.map((b, i) => (
              <motion.div
                key={b.heading}
                {...reveal}
                transition={{ ...reveal.transition, delay: i * 0.06 }}
                className="border-l-2 border-[#C5A059] pl-8"
                data-testid={`about-block-${i}`}
              >
                <h2 className="font-display text-2xl font-medium tracking-tight text-[#1C1917] md:text-3xl">
                  {renderLinkedText(b.heading)}
                </h2>
                <p className="mt-4 max-w-2xl whitespace-pre-line text-base leading-relaxed text-[#57534E]">
                  {renderLinkedText(b.text)}
                </p>
              </motion.div>
            ))}
          </div>
          <motion.div {...reveal} className="mt-20">
            <Link
              to="/contact"
              data-testid="about-contact-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#7A2B21] px-8 py-4 text-sm font-bold uppercase tracking-wider text-[#FAF8F5] transition-[background-color,transform] duration-300 hover:bg-[#5C1E16] hover:scale-[1.03]"
            >
              Talk to Our Team <ArrowRight size={15} />
            </Link>
          </motion.div>
        </section>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
